import BannerTrigger from '../../components/triggers/BannerTrigger';
import { useTriggers } from '../../contexts/TriggerContext';
// frontend/src/pages/Home/ForYouFeed.js - COMPLETE FIXED VERSION
import '../../pages/Create/CreateContent.css';
import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  CircularProgress,
  Alert,
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import { Send, ThumbUp, ChatBubbleOutline, Share } from '@mui/icons-material';
import InfiniteScroll from 'react-infinite-scroll-component';
import QuestionDisplay from '../../components/QuestionDisplay';
import WorkedExampleDisplay from '../../components/WorkedExampleDisplay';

const ForYouFeed = () => {
  const triggerCtx = useTriggers();
  const bannerTriggers = triggerCtx ? triggerCtx.getTriggersByPlacement('banner_top') : [];
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [postContent, setPostContent] = useState('');
  const [posting, setPosting] = useState(false);
  const [commentInputs, setCommentInputs] = useState({});

  // Track seen MongoDB _ids to prevent duplicates across pages
  const seenIdsRef = useRef(new Set());

  const { user } = useAuth();
  const prevGradeRef = useRef(user?.profile?.grade);

  useEffect(() => {
    const currentGrade = user?.profile?.grade;
    if (prevGradeRef.current !== undefined &&
        currentGrade !== undefined &&
        prevGradeRef.current !== currentGrade) {
      console.log(`[ForYouFeed] Grade changed ${prevGradeRef.current} -> ${currentGrade}, resetting feed`);
      setFeedItems([]);
      setHasMore(true);
      seenIdsRef.current.clear();
      setPage(1);
    }
    prevGradeRef.current = currentGrade;
  }, [user?.profile?.grade]);

  const API_URL = '';

  const fetchFeedData = async (pageNum = 1) => {
    try {
      const token = localStorage.getItem('token');

      // On first load, reset seen IDs
      if (pageNum === 1) {
        seenIdsRef.current.clear();
      }

      // Build excludeIds param from seen IDs (cap at 500 to keep URL manageable)
      const seenArray = Array.from(seenIdsRef.current);
      const excludeIds = seenArray.slice(-500).join(',');

      const url = pageNum === 1
        ? `${API_URL}/api/feed/for-you?page=${pageNum}&limit=20`
        : `${API_URL}/api/feed/for-you?page=${pageNum}&limit=20&excludeIds=${excludeIds}`;

      const response = await axios.get(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.data) {
        const newItems = response.data.items || [];

        // Track new MongoDB _ids
        newItems.forEach(item => {
          const mongoId = item.data?._id;
          if (mongoId) seenIdsRef.current.add(String(mongoId));
        });

        if (pageNum === 1) {
          setFeedItems(newItems);
        } else {
          // Client-side dedup as safety net
          setFeedItems(prev => {
            const existingIds = new Set(prev.map(item => item.id));
            const uniqueNew = newItems.filter(item => !existingIds.has(item.id));
            return [...prev, ...uniqueNew];
          });
        }

        setHasMore(response.data.hasMore);
        setError(null);
      }
    } catch (err) {
      console.error('Feed fetch error:', err);
      setError('Failed to load feed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedData(1);
  }, []);

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchFeedData(nextPage);
    }
  };

  const handleCreatePost = async () => {
    if (!postContent.trim() || posting) return;

    setPosting(true);
    try {
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_URL}/api/posts`,
        { content: postContent },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.data.success) {
        const newPost = response.data.post;
        const newFeedItem = {
          type: 'post',
          id: `post-${newPost._id}`,
          data: newPost,
          timestamp: newPost.created || new Date()
        };

        setFeedItems(prev => [newFeedItem, ...prev]);
        setPostContent('');
      }
    } catch (err) {
      console.error('Post creation error:', err);

      if (err.response?.data?.post) {
        const newPost = err.response.data.post;
        const newFeedItem = {
          type: 'post',
          id: `post-${newPost._id}`,
          data: newPost,
          timestamp: newPost.created || new Date()
        };

        setFeedItems(prev => [newFeedItem, ...prev]);
        setPostContent('');
      } else {
        alert('Failed to create post. Please try again.');
      }
    } finally {
      setPosting(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/posts/${postId}/like`,
        {},
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.data.success) {
        setFeedItems(prev => prev.map(item => {
          if (item.type === 'post') {
            const postData = item.data || item.content;

            if (postData && postData._id === postId) {
              if (item.data) {
                return {
                  ...item,
                  data: {
                    ...item.data,
                    likes: response.data.liked
                      ? [...(item.data.likes || []), 'user']
                      : (item.data.likes || []).filter(id => id !== 'user')
                  }
                };
              } else if (item.content) {
                return {
                  ...item,
                  content: {
                    ...item.content,
                    likes: response.data.liked
                      ? [...(item.content.likes || []), 'user']
                      : (item.content.likes || []).filter(id => id !== 'user')
                  }
                };
              }
            }
          }
          return item;
        }));
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleComment = async (postId) => {
    const comment = commentInputs[postId];
    if (!comment?.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/posts/${postId}/comment`,
        { content: comment },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.data.success) {
        setFeedItems(prev => prev.map(item => {
          if (item.type === 'post') {
            const postData = item.data || item.content;

            if (postData && postData._id === postId) {
              if (item.data) {
                return {
                  ...item,
                  data: {
                    ...item.data,
                    comments: [...(item.data.comments || []), response.data.comment]
                  }
                };
              } else if (item.content) {
                return {
                  ...item,
                  content: {
                    ...item.content,
                    comments: [...(item.content.comments || []), response.data.comment]
                  }
                };
              }
            }
          }
          return item;
        }));

        setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      }
    } catch (err) {
      console.error('Comment error:', err);
      alert('Failed to add comment');
    }
  };

  const handleAnswerQuestion = async (questionId, answer) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/questions/${questionId}/answer`,
        { answer },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      return response.data;
    } catch (error) {
      console.error('Answer submission error:', error);
      throw new Error('Failed to submit answer');
    }
  };

  const renderPost = (item) => {
    const post = item.data || item.content || item;

    if (!post) {
      console.error('Invalid post item:', item);
      return null;
    }

    const postId = post._id || post.id;

    return (
      <Paper key={item.id || postId} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight="bold">
            {post.user?.username || 'Anonymous'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {new Date(post.created || Date.now()).toLocaleDateString()}
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          {post.content || post.text || ''}
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <Button
            size="small"
            startIcon={<ThumbUp />}
            onClick={() => handleLikePost(postId)}
            color={(post.likes?.length || 0) > 0 ? 'primary' : 'inherit'}
          >
            Like {(post.likes?.length || 0) > 0 && `(${post.likes.length})`}
          </Button>

          <Button
            size="small"
            startIcon={<ChatBubbleOutline />}
          >
            Comment {(post.comments?.length || 0) > 0 && `(${post.comments.length})`}
          </Button>

          <Button
            size="small"
            startIcon={<Share />}
          >
            Share
          </Button>
        </Box>

        {post.comments && post.comments.length > 0 && (
          <Box mt={2}>
            <Divider sx={{ my: 1 }} />
            {post.comments.map((comment, idx) => (
              <Box key={idx} sx={{ ml: 2, mt: 1 }}>
                <Typography variant="caption" fontWeight="bold">
                  {comment.user?.username || 'Anonymous'}:
                </Typography>
                <Typography variant="body2">
                  {comment.content || comment.text || ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box display="flex" mt={2}>
          <TextField
            size="small"
            fullWidth
            placeholder="Write a comment..."
            value={commentInputs[postId] || ''}
            onChange={(e) => setCommentInputs(prev => ({
              ...prev,
              [postId]: e.target.value
            }))}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleComment(postId);
              }
            }}
          />
          <IconButton
            size="small"
            onClick={() => handleComment(postId)}
            disabled={!commentInputs[postId]?.trim()}
          >
            <Send />
          </IconButton>
        </Box>
      </Paper>
    );
  };

  const renderFeedItem = (item) => {
    if (!item) return null;

    const itemContent = item.data || item.content;

    if (!itemContent) {
      console.warn('Invalid feed item - no data or content:', item);
      return null;
    }

    switch (item.type) {
      case 'post':
        return renderPost(item);

      case 'question':
        return (
          <Box key={item.id || itemContent._id} mb={2}>
            <QuestionDisplay
              question={itemContent}
              onAnswer={async (answer) => {
                const questionId = itemContent._id || item.id;
                return handleAnswerQuestion(questionId, answer);
              }}
              onSkip={() => console.log('Skip:', itemContent._id)}
            />
          </Box>
        );

      case 'workedExample':
        return (
          <Box key={item.id || itemContent._id} mb={2}>
            <WorkedExampleDisplay
              example={itemContent}
              onRate={(rating) => console.log('Rate:', itemContent._id, rating)}
              onBookmark={() => console.log('Bookmark:', itemContent._id)}
            />
          </Box>
        );

      default:
        console.warn('Unknown item type:', item.type);
        return null;
    }
  };

  if (loading && feedItems.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Post Composer */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="What's on your mind?"
          value={postContent}
          onChange={(e) => setPostContent(e.target.value)}
          variant="outlined"
        />
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="contained"
            onClick={handleCreatePost}
            disabled={!postContent.trim() || posting}
          >
            {posting ? 'Posting...' : 'Post'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Feed Items */}
      <InfiniteScroll
        dataLength={feedItems.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={30} />
          </Box>
        }
        endMessage={
          <Box textAlign="center" p={2} color="text.secondary">
            <Typography variant="body2">
              You've reached the end of your feed!
            </Typography>
          </Box>
        }
      >
        {feedItems.map((item, index) => (
          <div key={item.id || `item-${index}`}>
            {renderFeedItem(item)}
          
      <button className="create-fab" onClick={() => navigate('/create')} title="Create content">
        <i className="fas fa-plus"></i>
      </button>
    </div>
        ))}
      </InfiniteScroll>
    </Box>
  );
};

export default ForYouFeed;
